import { ArrowRight } from 'lucide-react';
import { Link } from '@/i18n/navigation';
import { BlogCardMedia, BlogCardMeta, type BlogCard } from '@/components/sections/blog-card-parts';
import { Card, CardContent, CardTitle, CardDescription } from '@/components/ui/card';
import { TypographyOverline } from '@/components/ui/typography';

export type { BlogCard };

/**
 * Presentational article card — image header, topic label, serif title,
 * excerpt, and a date · read-time footer with a direction-aware arrow.
 * Pure (no hooks), so both the server index page and the client teaser
 * can render it.
 */
export function BlogPostCard({ post }: { post: BlogCard }) {
  return (
    <Link href={`/blog/${post.slug}`} className="group block h-full">
      <Card className="group flex h-full flex-col gap-0 overflow-hidden py-0 shadow-card transition-brand group-hover:-translate-y-1 group-hover:shadow-raised border-border/60">
        <BlogCardMedia
          post={post}
          sizes="(min-width: 1024px) 30vw, (min-width: 640px) 45vw, 90vw"
          imageClassName="object-top"
        />
        <CardContent className="flex flex-1 flex-col p-6">
          <TypographyOverline className="text-brand-gold">{post.categoryLabel}</TypographyOverline>
          <CardTitle className="mt-3 text-h4 font-medium text-primary">{post.title}</CardTitle>
          <CardDescription className="mt-3 line-clamp-2">{post.description}</CardDescription>
          <div className="mt-auto flex items-center justify-between pt-6">
            <BlogCardMeta post={post} />
            <ArrowRight
              aria-hidden
              data-directional-icon-decorative
              className="size-5 text-brand-gold transition-transform group-hover:translate-x-1 rtl:-scale-x-100 rtl:group-hover:-translate-x-1"
            />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
